import React, { useContext, useEffect, useRef, useState, useCallback } from "react";
import { Animated, StyleSheet, View as RNView, Dimensions, Platform } from "react-native";
import { Modal, Text, TouchableOpacity, View, Pressable } from "react-native";
import { Feather } from "@expo/vector-icons";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { DataTable, ThemeProvider, useTheme as usePaperTheme } from "react-native-paper";
import { ScrollView } from "react-native-gesture-handler";
import Spinner from "../../../components/shared/Spinner";
import DeviceOrientationContext from "../../../components/context/DeviceOrientationContext";
import { useDispatch, useSelector } from "react-redux";
import { LabelBannerWithSearchBar } from "../../../components/custom/Banners/LabelBannerWithSearchBar";
import { useIsFocused } from "@react-navigation/native";
import {
  translateFields,
  translateHeaders,
  useTranslation,
} from "../../../utils/useTranslation";
import { chinaTrackerTranslationMap } from "../../../utils/translations";
import { ChinaTrackerDataTableWithPagination2 } from "../../../components/custom/DataTable/tracker/ChinaTrackerDataTableWithPagination2";
import {
  filterAllSearch,
  resetAllSearch,
  searchAllTrackerOrders,
} from "../../../redux/reducers/chinaTrackerReducer";
import { getMergedTrackerOrders } from "../../../redux/dispatcher/chinaTrackerDispatcher";
import { useAppTheme } from "../../../hooks/useAppTheme";


interface TrackerEnquiryModalProps {
  visible: boolean;
  onClose: any;
  order: string;
}

// Define headers for the data table
const tableHeadersData = {
  MSRef: {
    name: "MS No.",
    width: 0.3,
    fontWeight: 700,
  },
  OrderNumber1: {
    name: "Order Number",
    width: 0.3,
    fontWeight: 700,
  },
  ItemCode: {
    name: "Item Code",
    width: 0.3,
  },
  ItemName: {
    name: "Item Name",
    width: 0.8,
  },
  BPName: {
    name: "Customer",
    width: 0.8,
  },
  StateDelivery: {
    name: "State",
    width: 0.2,
    fontWeight: 700,
  },
  OrderDate: {
    name: "PO Order Date",
    width: 0.3,
    fontWeight: 700,
  },
  PriorityOrder: {
    name: "Priority Order",
    width: 0.3,
    fontWeight: 700,
  },
  RequestDate: {
    name: "Due Date",
    width: 0.3,
    fontWeight: 700,
  },
  TotalOrdered: {
    name: "Order Qty (UoM)",
    width: 0.3,
    fontWeight: 700,
  },
  OrderStatus: {
    name: "Status",
    width: 0.4,
    fontWeight: 700,
  },
  incomingStatus: {
    name: "Incoming Status",
    width: 0.4,
    fontWeight: 700,
  },
};

const ChinaTrackerAdvancedSearch: React.FC<TrackerEnquiryModalProps> = ({
  visible,
  onClose,
  order,
}: any) => {
  const [screenWidth, setScreenWidth] = useState(
    Dimensions.get("window").width,
  );
  const [screenHeight, setScreenHeight] = useState(
    Dimensions.get("window").height,
  );
  const [tableHeaders, setTableHeaders] =
    useState<Partial<typeof tableHeadersData>>(tableHeadersData);

  const {
    allOrders: orders,
    loading,
    loadingIncomingAdd,
  } = useSelector((state: any) => state.chinaTracker);
  const { t } = useTranslation();
  const [isSearched, setIsSearched] = useState<boolean>(false);
  const [page, setPage] = useState<number>(0);
  const dispatch = useDispatch<any>();
  const [actionRows, setActionRows] = useState<any>([]);
  const [isRefresh, setRefresh] = useState<boolean>(false);
  const { isLargeDevice, isMediumDevice, isSmallDevice } = useContext(
    DeviceOrientationContext,
  );
  const [tableScrollOffset, setTableScrollOffset] = useState(0);

  // Column visibility settings
  const [columnSettingsOpen, setColumnSettingsOpen] = useState<boolean>(false);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [columnOrder, setColumnOrder] = useState<string[]>([]);
  const [settingsBtnPos, setSettingsBtnPos] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const settingsBtnRef = useRef<any>(null);
  const { colors: themeColors } = useAppTheme();
  const styles = createStyles(themeColors);
  const paperTheme = usePaperTheme();
  const paginationTheme = { ...paperTheme, colors: { ...paperTheme.colors, text: themeColors.text, onSurface: themeColors.text, onSurfaceVariant: themeColors.text } };

  // Load saved hidden columns
  useEffect(() => {
    AsyncStorage.getItem("chinatracker_advanced_hidden_columns").then(
      (stored) => {
        if (stored) {
          try {
            setHiddenColumns(JSON.parse(stored));
          } catch {}
        }
      },
    );
  }, []);

  const getOrderedKeys = (baseHeaders: Record<string, Record<string, string | number | boolean>>) => {
    const allKeys = Object.keys(baseHeaders);
    if (columnOrder.length === 0) return allKeys;
    const ordered: string[] = [];
    columnOrder.forEach((k) => { if (allKeys.includes(k)) ordered.push(k); });
    allKeys.forEach((k) => { if (!ordered.includes(k)) ordered.push(k); });
    return ordered;
  };

  const getFilteredHeaders = (baseHeaders: Record<string, any>) => {
    const orderedKeys = getOrderedKeys(baseHeaders);
    const visibleEntries = orderedKeys
      .filter((key) => !hiddenColumns.includes(key))
      .map((key) => [key, baseHeaders[key]] as [string, Record<string, string | number | boolean>]);
    const totalOriginalFlex = Object.values(baseHeaders).reduce((sum, h) => sum + (Number(h.width) || 0), 0);
    const totalVisibleFlex = visibleEntries.reduce((sum, [, h]) => sum + (Number(h.width) || 0), 0);
    const scale = totalVisibleFlex > 0 ? totalOriginalFlex / totalVisibleFlex : 1;

    const result: Record<string, any> = {};
    visibleEntries.forEach(([key, value]) => {
      result[key] = { ...value, width: Number(value.width) * scale };
    });
    return result;
  };

  const toggleColumn = (columnKey: string) => {
    setHiddenColumns((prev) => {
      const updated = prev.includes(columnKey)
        ? prev.filter((k) => k !== columnKey)
        : [...prev, columnKey];
      AsyncStorage.setItem(
        "chinatracker_advanced_hidden_columns",
        JSON.stringify(updated),
      );
      return updated;
    });
  };

  const moveColumn = useCallback((fromKey: string, toKey: string) => {
    const allHeaders = tableHeadersData as Record<string, any>;
    const currentKeys = getOrderedKeys(allHeaders).filter((k) => k !== "");
    const fromIdx = currentKeys.indexOf(fromKey);
    const toIdx = currentKeys.indexOf(toKey);
    if (fromIdx < 0 || toIdx < 0 || fromIdx === toIdx) return;
    const newKeys = [...currentKeys];
    newKeys.splice(fromIdx, 1);
    newKeys.splice(toIdx, 0, fromKey);
    newKeys.push("");
    setColumnOrder(newKeys);
    AsyncStorage.setItem("china_tracker_search_column_order", JSON.stringify(newKeys));
  }, [columnOrder]);

  const resetColumnOrder = () => {
    setColumnOrder([]);
    AsyncStorage.removeItem("china_tracker_search_column_order");
  };

  // Drag-and-drop reordering via window mouse events
  const [dragActiveKey, setDragActiveKey] = useState<string | null>(null);
  const [dragOverKey, setDragOverKey] = useState<string | null>(null);
  const dragRef = useRef<{ activeKey: string | null; overKey: string | null }>({ activeKey: null, overKey: null });

  const startColumnDrag = useCallback((key: string) => {
    if (Platform.OS !== "web" || typeof window === "undefined") return;
    dragRef.current.activeKey = key;
    setDragActiveKey(key);

    const onMouseMove = (e: MouseEvent) => {
      const el = document.elementFromPoint(e.clientX, e.clientY);
      const row = el && (el as HTMLElement).closest ? (el as HTMLElement).closest("[data-columnkey]") : null;
      const overKey = row ? row.getAttribute("data-columnkey") : null;
      if (overKey !== dragRef.current.overKey) {
        dragRef.current.overKey = overKey;
        setDragOverKey(overKey && overKey !== dragRef.current.activeKey ? overKey : null);
      }
    };

    const onMouseUp = () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
      document.body.style.cursor = "";
      (document.body.style as any).userSelect = "";
      const from = dragRef.current.activeKey;
      const to = dragRef.current.overKey;
      if (from && to && from !== to) {
        moveColumn(from, to);
      }
      dragRef.current = { activeKey: null, overKey: null };
      setDragActiveKey(null);
      setDragOverKey(null);
    };

    document.body.style.cursor = "grabbing";
    (document.body.style as any).userSelect = "none";
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  }, [moveColumn]);

  const fromPage = (page: number, itemPerPage: number, rows: any[] | undefined) => {
    return page * itemPerPage + itemPerPage > (rows || []).length
      ? (rows || []).length
      : page * itemPerPage + itemPerPage;
  };

  const [dateFilter, setDateFilter] = useState<string[]>(["all"]);

  const toggleDateFilter = (filter: string) => {
    if (filter === "all") {
      setDateFilter(["all"]);
    } else {
      setDateFilter((prev) => {
        let newFilters = prev.filter((f) => f !== "all");
        if (newFilters.includes(filter)) {
          newFilters = newFilters.filter((f) => f !== filter);
        } else {
          newFilters.push(filter);
        }
        if (newFilters.length === 0) return ["all"];
        return newFilters;
      });
    }
  };

  // Update dimensions when screen size changes
  useEffect(() => {
    const updateDimensions = () => {
      const { width, height } = Dimensions.get("window");
      setScreenWidth(width);
      setScreenHeight(height);
    };

    const dimensionsListener = Dimensions.addEventListener(
      "change",
      updateDimensions,
    );

    return () => {
      dimensionsListener.remove();
    };
  }, []);

  // Calculate modal width based on screen size
  const getModalWidth = () => {
    if (isSmallDevice) return "100%";
    if (isMediumDevice) return "94%";
    return "92%";
  };

  // Calculate inner container width based on screen size
  const getInnerContainerWidth = () => {
    if (isSmallDevice) return "100%";
    return "100%";
  };

  const handleSearch = (searchedText: string) => {
    setPage(0);
    if (searchedText) {
      dispatch(searchAllTrackerOrders(searchedText));
    } else {
      dispatch(resetAllSearch());
    }
  };
  const isFocused = useIsFocused();

  useEffect(() => {
    let isMounted: boolean = true;
    if (isRefresh) {
      dispatch(getMergedTrackerOrders({ refresh: true, showLoading: false }));
      setRefresh(false);
    }

    return () => {
      isMounted = false;
    };
  }, [isRefresh, isLargeDevice, isMediumDevice, isSmallDevice, screenWidth]);

  useEffect(() => {
    setRefresh(false);
    setPage(0);
    setTableHeaders(
      translateHeaders(tableHeadersData, t, chinaTrackerTranslationMap),
    );
    if (dateFilter) {
      dispatch(filterAllSearch(dateFilter));
    }
  }, [isFocused, isRefresh, dateFilter]);

  useEffect(() => {
    setTableHeaders(
      translateHeaders(tableHeadersData, t, chinaTrackerTranslationMap),
    );
  }, []);

  return (
    <>
      <Modal visible={visible} transparent animationType="slide">
        <View style={styles.overlay}>
          {isLargeDevice ? (
            <View style={[styles.modalContainer, { width: getModalWidth() }]}>
              <View
                style={[
                  styles.modalContainerSecond,
                  {
                    width: getInnerContainerWidth() as any,
                    height: "100%" as any,
                  },
                ]}
              >
                <View style={styles.stickyHeaderSection}>
                  <View
                    style={[
                      styles.titleContainer,
                      isSmallDevice && {
                        flexDirection: "column",
                      },
                    ]}
                  >
                    <Text
                      style={[
                        styles.titleText,
                        isSmallDevice && styles.titleTextSmall,
                      ]}
                    >
                      {t("allOrders")}
                    </Text>
                    {!isSmallDevice && (
                      <View style={[styles.searchContainer]}>
                        <LabelBannerWithSearchBar
                          titleName={``}
                          handleSearch={handleSearch}
                          setRefresh={setRefresh}
                          dataToExport={orders}
                          fileName="ChinaTrackerAllOrders"
                          sheetName="China Tracker All Orders Screen"
                          preSearchControls={
                            <TouchableOpacity
                              ref={settingsBtnRef}
                              style={{
                                width: 40,
                                height: 40,
                                borderRadius: 10,
                                backgroundColor: themeColors.surfaceSubtle,
                                justifyContent: "center",
                                alignItems: "center",
                                borderWidth: 1,
                                borderColor: columnSettingsOpen
                                  ? "#818CF8"
                                  : themeColors.border,
                              }}
                              onPress={() => {
                                if (settingsBtnRef.current) {
                                  settingsBtnRef.current.measureInWindow(
                                    (
                                      x: number,
                                      y: number,
                                      width: number,
                                      height: number,
                                    ) => {
                                      setSettingsBtnPos({
                                        x: x || 0,
                                        y: y || 0,
                                        width: width || 0,
                                        height: height || 0,
                                      });
                                      setColumnSettingsOpen(
                                        !columnSettingsOpen,
                                      );
                                    },
                                  );
                                } else {
                                  setColumnSettingsOpen(!columnSettingsOpen);
                                }
                              }}
                            >
                              <Feather
                                name="settings"
                                size={15}
                                color={
                                  columnSettingsOpen ? themeColors.primary : themeColors.textSecondary
                                }
                              />
                            </TouchableOpacity>
                          }
                        />
                      </View>
                    )}
                  </View>
                </View>

                <View
                  style={[{ marginLeft: 15, marginTop: 10, marginBottom: 10 }]}
                >
                  <ScrollView horizontal>
                    <TouchableOpacity onPress={() => toggleDateFilter("all")}>
                      <Text
                        style={
                          dateFilter.includes("all")
                            ? styles.selectedFilter
                            : styles.filterLabel
                        }
                      >
                        {t("all")}
                      </Text>
                    </TouchableOpacity>

                    <TouchableOpacity
                      onPress={() => toggleDateFilter("cancelled")}
                    >
                      <Text
                        style={
                          dateFilter.includes("cancelled")
                            ? styles.selectedFilter
                            : styles.filterLabel
                        }
                      >
                        {t("cancelled")}
                      </Text>
                    </TouchableOpacity>

                    <TouchableOpacity onPress={() => toggleDateFilter("closed")}>
                      <Text
                        style={
                          dateFilter.includes("closed")
                            ? styles.selectedFilter
                            : styles.filterLabel
                        }
                      >
                        {t("closed")}
                      </Text>
                    </TouchableOpacity>

                    <TouchableOpacity onPress={() => toggleDateFilter("onTime")}>
                      <Text
                        style={
                          dateFilter.includes("onTime")
                            ? styles.selectedFilter
                            : styles.filterLabel
                        }
                      >
                        {t("onTime")}
                      </Text>
                    </TouchableOpacity>

                    <TouchableOpacity onPress={() => toggleDateFilter("noData")}>
                      <Text
                        style={
                          dateFilter.includes("noData")
                            ? styles.selectedFilter
                            : styles.filterLabel
                        }
                      >
                        {t("noData")}
                      </Text>
                    </TouchableOpacity>

                    <TouchableOpacity
                      onPress={() => toggleDateFilter("interrupted")}
                    >
                      <Text
                        style={
                          dateFilter.includes("interrupted")
                            ? styles.selectedFilter
                            : styles.filterLabel
                        }
                      >
                        {t("interrupted")}
                      </Text>
                    </TouchableOpacity>

                    <TouchableOpacity
                      onPress={() => toggleDateFilter("planned")}
                    >
                      <Text
                        style={
                          dateFilter.includes("planned")
                            ? styles.selectedFilter
                            : styles.filterLabel
                        }
                      >
                        {t("planned")}
                      </Text>
                    </TouchableOpacity>

                    <TouchableOpacity
                      onPress={() => toggleDateFilter("incoming")}
                    >
                      <Text
                        style={
                          dateFilter.includes("incoming")
                            ? styles.selectedFilter
                            : styles.filterLabel
                        }
                      >
                        {t("incoming")}
                      </Text>
                    </TouchableOpacity>

                    <TouchableOpacity
                      onPress={() => toggleDateFilter("approved")}
                    >
                      <Text
                        style={
                          dateFilter.includes("approved")
                            ? styles.selectedFilter
                            : styles.filterLabel
                        }
                      >
                        {t("approved")}
                      </Text>
                    </TouchableOpacity>

                    <TouchableOpacity
                      onPress={() => toggleDateFilter("completed")}
                    >
                      <Text
                        style={
                          dateFilter.includes("completed")
                            ? styles.selectedFilter
                            : styles.filterLabel
                        }
                      >
                        {t("completed")}
                      </Text>
                    </TouchableOpacity>

                    <TouchableOpacity onPress={() => toggleDateFilter("atRisk")}>
                      <Text
                        style={
                          dateFilter.includes("atRisk")
                            ? styles.selectedFilter
                            : styles.filterLabel
                        }
                      >
                        {t("atRisk")}
                      </Text>
                    </TouchableOpacity>

                    <TouchableOpacity
                      onPress={() => toggleDateFilter("delayed")}
                    >
                      <Text
                        style={
                          dateFilter.includes("delayed")
                            ? styles.selectedFilter
                            : styles.filterLabel
                        }
                      >
                        {t("delayed")}
                      </Text>
                    </TouchableOpacity>
                  </ScrollView>
                </View>

                <ScrollView
                  style={styles.modalContentScroll}
                  contentContainerStyle={styles.modalContentScrollContainer}
                >
                  <View style={styles.scrollBodySection}>
                    <View style={styles.tableScrollWrapper}>
                      <Animated.ScrollView
                        horizontal
                        showsHorizontalScrollIndicator
                        contentContainerStyle={{
                          minWidth: Math.max(screenWidth, 1200),
                        }}
                        scrollEventThrottle={16}
                        onScroll={(event) =>
                          setTableScrollOffset(
                            event.nativeEvent.contentOffset.x || 0,
                          )
                        }
                      >
                        <View
                          style={[
                            styles.containerContent,
                            { minWidth: Math.max(screenWidth, 1200) },
                          ]}
                        >
                          {!(loading || loadingIncomingAdd) ? (
                            <View>
                              <ChinaTrackerDataTableWithPagination2
                                headers={getFilteredHeaders(tableHeaders)}
                                rows={orders}
                                itemsPerPage={10}
                                isDataLoading={isSearched}
                                isDisplayTimeWithDate={true}
                                useModal={true}
                                isDetail={false}
                                isEdit={false}
                                isDelete={false}
                                actionRows={actionRows}
                                setActionRows={setActionRows}
                                itemTracker
                                // Required props
                                setModalVisible={() => {}}
                                handleRowClick={() => {}}
                                setSelected={() => {}}
                                handleDeleteRow={() => {}}
                                operationActions={() => {}}
                                selectable={true}
                                nestedHeader={null}
                                t={t}
                                page={page}
                                setPage={setPage}
                                hidePagination={true}
                              />
                            </View>
                          ) : (
                            <View>
                              <Spinner
                                size="small"
                                color="#74BBFF"
                                styles={{ flexDirection: "row", margin: "20%" }}
                              />
                            </View>
                          )}
                        </View>
                      </Animated.ScrollView>
                    </View>
                  </View>
                </ScrollView>
                <View
                  style={{
                    borderTopWidth: 1,
                    borderTopColor: themeColors.border,
                    backgroundColor: themeColors.surface,
                  }}
                >
                  <ThemeProvider theme={paginationTheme}>
<DataTable.Pagination 
                    page={page}
                    numberOfPages={Math.ceil((orders?.length || 0) / 10)}
                    onPageChange={(p) => setPage(p)}
                    label={`${fromPage(page, 10, orders)} of ${(orders?.length || 0)}`}
                    showFastPaginationControls
                    numberOfItemsPerPage={10}
                    selectPageDropdownLabel={"Rows per page"}
                  />
</ThemeProvider>
                </View>
                <View
                  style={[styles.footer, isSmallDevice && styles.footerSmall]}
                >
                  <TouchableOpacity
                    onPress={onClose}
                    style={[
                      styles.closeButton,
                      isSmallDevice && styles.buttonSmall,
                    ]}
                  >
                    <Text style={[styles.closeButtonText, isSmallDevice && styles.buttonTextSmall]}>
                      {t("close")}
                    </Text>
                  </TouchableOpacity>
                </View>
              </View>
            </View>
          ) : (
            <View
              style={[
                styles.modalContainerSecond,
                {
                  width: getInnerContainerWidth() as any,
                  height: "95%" as any,
                },
                isSmallDevice && styles.modalContainerMobile,
              ]}
            >
              <View
                style={[
                  styles.titleContainer,
                  isSmallDevice && styles.titleContainerMobile,
                ]}
              >
                <Text
                  style={[
                    styles.titleText,
                    isSmallDevice && styles.titleTextSmall,
                  ]}
                >
                  {t("allOrders")}
                </Text>
                <View
                  style={[
                    styles.searchContainer,
                    isSmallDevice && styles.searchContainerMobile,
                  ]}
                >
                  <LabelBannerWithSearchBar
                    titleName={``}
                    handleSearch={handleSearch}
                    setRefresh={setRefresh}
                    dataToExport={orders}
                    fileName="ChinaTrackerRejectedOrders"
                    sheetName="China Tracker Reject Report Screen"

                  />
                </View>
              </View>

              <View
                style={[{ marginLeft: 15, marginTop: 10, marginBottom: 10 }]}
              >
                <ScrollView horizontal>
                  <TouchableOpacity onPress={() => toggleDateFilter("all")}>
                    <Text
                      style={
                        dateFilter.includes("all")
                          ? styles.selectedFilter
                          : styles.filterLabel
                      }
                    >
                      {t("all")}
                    </Text>
                  </TouchableOpacity>

                  <TouchableOpacity
                    onPress={() => toggleDateFilter("cancelled")}
                  >
                    <Text
                      style={
                        dateFilter.includes("cancelled")
                          ? styles.selectedFilter
                          : styles.filterLabel
                      }
                    >
                      {t("cancelled")}
                    </Text>
                  </TouchableOpacity>

                  <TouchableOpacity onPress={() => toggleDateFilter("closed")}>
                    <Text
                      style={
                        dateFilter.includes("closed")
                          ? styles.selectedFilter
                          : styles.filterLabel
                      }
                    >
                      {t("closed")}
                    </Text>
                  </TouchableOpacity>

                  <TouchableOpacity onPress={() => toggleDateFilter("onTime")}>
                    <Text
                      style={
                        dateFilter.includes("onTime")
                          ? styles.selectedFilter
                          : styles.filterLabel
                      }
                    >
                      {t("onTime")}
                    </Text>
                  </TouchableOpacity>

                  <TouchableOpacity onPress={() => toggleDateFilter("noData")}>
                    <Text
                      style={
                        dateFilter.includes("noData")
                          ? styles.selectedFilter
                          : styles.filterLabel
                      }
                    >
                      {t("noData")}
                    </Text>
                  </TouchableOpacity>

                  <TouchableOpacity
                    onPress={() => toggleDateFilter("interrupted")}
                  >
                    <Text
                      style={
                        dateFilter.includes("interrupted")
                          ? styles.selectedFilter
                          : styles.filterLabel
                      }
                    >
                      {t("interrupted")}
                    </Text>
                  </TouchableOpacity>

                  <TouchableOpacity
                    onPress={() => toggleDateFilter("planned")}
                  >
                    <Text
                      style={
                        dateFilter.includes("planned")
                          ? styles.selectedFilter
                          : styles.filterLabel
                      }
                    >
                      {t("planned")}
                    </Text>
                  </TouchableOpacity>

                  <TouchableOpacity
                    onPress={() => toggleDateFilter("incoming")}
                  >
                    <Text
                      style={
                        dateFilter.includes("incoming")
                          ? styles.selectedFilter
                          : styles.filterLabel
                      }
                    >
                      {t("incoming")}
                    </Text>
                  </TouchableOpacity>

                  <TouchableOpacity
                    onPress={() => toggleDateFilter("approved")}
                  >
                    <Text
                      style={
                        dateFilter.includes("approved")
                          ? styles.selectedFilter
                          : styles.filterLabel
                      }
                    >
                      {t("approved")}
                    </Text>
                  </TouchableOpacity>

                  <TouchableOpacity
                    onPress={() => toggleDateFilter("completed")}
                  >
                    <Text
                      style={
                        dateFilter.includes("completed")
                          ? styles.selectedFilter
                          : styles.filterLabel
                      }
                    >
                      {t("completed")}
                    </Text>
                  </TouchableOpacity>

                  <TouchableOpacity onPress={() => toggleDateFilter("atRisk")}>
                    <Text
                      style={
                        dateFilter.includes("atRisk")
                          ? styles.selectedFilter
                          : styles.filterLabel
                      }
                    >
                      {t("atRisk")}
                    </Text>
                  </TouchableOpacity>

                  <TouchableOpacity
                    onPress={() => toggleDateFilter("delayed")}
                  >
                    <Text
                      style={
                        dateFilter.includes("delayed")
                          ? styles.selectedFilter
                          : styles.filterLabel
                      }
                    >
                      {t("delayed")}
                    </Text>
                  </TouchableOpacity>
                </ScrollView>
              </View>

              <ScrollView
                style={styles.modalContentScroll}
                contentContainerStyle={styles.modalContentScrollContainer}
              >
                <View
                  style={[
                    styles.scrollBodySection,
                    isSmallDevice && styles.scrollBodySectionMobile,
                  ]}
                >
                  <View style={styles.tableScrollWrapper}>
                    <Animated.ScrollView
                      horizontal
                      showsHorizontalScrollIndicator
                      contentContainerStyle={{
                        minWidth: Math.max(screenWidth, 1200),
                      }}
                      scrollEventThrottle={16}
                      onScroll={(event) =>
                        setTableScrollOffset(
                          event.nativeEvent.contentOffset.x || 0,
                        )
                      }
                    >
                      <View
                        style={[
                          styles.containerContent,
                          { minWidth: Math.max(screenWidth, 1200) },
                        ]}
                      >
                        {!(loading || loadingIncomingAdd) ? (
                          <View>
                            <ChinaTrackerDataTableWithPagination2
                              headers={getFilteredHeaders(tableHeaders)}
                              rows={orders}
                              itemsPerPage={10}
                              isDataLoading={isSearched}
                              isDisplayTimeWithDate={true}
                              useModal={true}
                              isDetail={false}
                              isEdit={false}
                              isDelete={false}
                              actionRows={actionRows}
                              setActionRows={setActionRows}
                              itemTracker
                              // Required props
                              setModalVisible={() => {}}
                              handleRowClick={() => {}}
                              setSelected={() => {}}
                              handleDeleteRow={() => {}}
                              page={page}
                              setPage={setPage}
                              selectable={true}
                              hidePagination={true}
                            />
                          </View>
                        ) : (
                          <View>
                            <Spinner
                              size="small"
                              color="#74BBFF"
                              styles={{ flexDirection: "row", margin: "20%" }}
                            />
                          </View>
                        )}
                      </View>
                    </Animated.ScrollView>
                  </View>
                </View>
              </ScrollView>
              <View
                style={{
                  borderTopWidth: 1,
                  borderTopColor: themeColors.border,
                  backgroundColor: themeColors.surface,
                }}
              >
                <ThemeProvider theme={paginationTheme}>
<DataTable.Pagination 
                  page={page}
                  numberOfPages={Math.ceil((orders?.length || 0) / 10)}
                  onPageChange={(p) => setPage(p)}
                  label={`${fromPage(page, 10, orders)} of ${(orders?.length || 0)}`}
                  showFastPaginationControls
                  numberOfItemsPerPage={10}
                  selectPageDropdownLabel={"Rows per page"}
                />
</ThemeProvider>
              </View>
              <View
                style={[styles.footer, isSmallDevice && styles.footerSmall]}
              >
                <TouchableOpacity
                  onPress={onClose}
                  style={[
                    styles.closeButton,
                    isSmallDevice && styles.buttonSmall,
                  ]}
                >
                  <Text style={[styles.closeButtonText, isSmallDevice && styles.buttonTextSmall]}>
                    {t("close")}
                  </Text>
                </TouchableOpacity>
              </View>
            </View>
          )}
        </View>
      </Modal>

      {columnSettingsOpen && (
        <Modal
          transparent={true}
          visible={columnSettingsOpen}
          animationType="fade"
          onRequestClose={() => setColumnSettingsOpen(false)}
        >
          <TouchableOpacity
            style={{
              flex: 1,
              backgroundColor: "rgba(0,0,0,0.1)",
            }}
            activeOpacity={1}
            onPress={() => setColumnSettingsOpen(false)}
          >
            <TouchableOpacity
              activeOpacity={1}
              style={{
                position: "absolute",
                top: isSmallDevice
                  ? "50%"
                  : settingsBtnPos.y + settingsBtnPos.height + 6,
                left: isSmallDevice ? "50%" : settingsBtnPos.x - 180 + 40,
                transform: isSmallDevice
                  ? [{ translateX: -110 }, { translateY: -150 }]
                  : [],
                backgroundColor: themeColors.surface,
                borderRadius: 12,
                borderWidth: 1,
                borderColor: themeColors.border,
                paddingVertical: 10,
                minWidth: 220,
                shadowColor: themeColors.text,
                shadowOffset: { width: 0, height: 8 },
                shadowOpacity: 0.15,
                shadowRadius: 20,
                elevation: 10,
                zIndex: 9999,
              }}
              onPress={(e) => e.stopPropagation()}
            >
              <Text
                style={{
                  fontSize: 12,
                  fontWeight: "700",
                  color: themeColors.textSecondary,
                  paddingHorizontal: 16,
                  paddingBottom: 8,
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                }}
              >
                {t("showHideColumns")}
              </Text>
              <View
                style={{
                  height: 1,
                  backgroundColor: themeColors.surfaceSubtle,
                  marginBottom: 4,
                }}
              />
              {/* Select All */}
              <TouchableOpacity
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  paddingVertical: 8,
                  paddingHorizontal: 16,
                }}
                onPress={() => {
                  const allKeys = Object.keys(tableHeaders).filter(
                    (k) => k !== "",
                  );
                  if (hiddenColumns.length === 0) {
                    const updated = [...allKeys];
                    setHiddenColumns(updated);
                    AsyncStorage.setItem(
                      "chinatracker_advanced_hidden_columns",
                      JSON.stringify(updated),
                    );
                  } else {
                    setHiddenColumns([]);
                    AsyncStorage.setItem(
                      "chinatracker_advanced_hidden_columns",
                      JSON.stringify([]),
                    );
                  }
                }}
              >
                <View
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: 4,
                    borderWidth: 1.5,
                    borderColor: hiddenColumns.length === 0 ? themeColors.primary : themeColors.textSecondary,
                    backgroundColor:
                      hiddenColumns.length === 0 ? themeColors.primary : themeColors.surface,
                    justifyContent: "center",
                    alignItems: "center",
                    marginRight: 10,
                  }}
                >
                  {hiddenColumns.length === 0 && (
                    <Feather name="check" size={13} color={themeColors.surface} />
                  )}
                </View>
                <Text
                  style={{
                    fontSize: 14,
                    color: themeColors.text,
                    fontWeight: "700",
                  }}
                >
                  Select All
                </Text>
              </TouchableOpacity>
              <View style={{ height: 1, backgroundColor: themeColors.surfaceSubtle, marginBottom: 2 }} />
              <ScrollView style={{ maxHeight: 300 }} showsVerticalScrollIndicator={true}>
                {getOrderedKeys(tableHeaders as any).map((key) => {
                  if (key === "") return null;
                  const value = (tableHeaders as any)[key];
                  if (!value) return null;
                  if ((value as any).display === "none" || (value as any).display === "hidden") return null;
                  const isVisible = !hiddenColumns.includes(key);
                  const isDragging = dragActiveKey === key;
                  const isDragOver = dragOverKey === key;
                  return (
                    <RNView
                      key={key}
                      {...{ dataSet: { columnkey: key } } as any}
                      style={{
                        flexDirection: "row",
                        alignItems: "center",
                        paddingVertical: 8,
                        paddingHorizontal: 16,
                        opacity: isDragging ? 0.35 : 1,
                        backgroundColor: isDragOver ? themeColors.primaryBg : "transparent",
                        borderTopWidth: isDragOver ? 2 : 0,
                        borderTopColor: themeColors.primary,
                      }}
                    >
                      <Pressable
                        onPressIn={() => startColumnDrag(key)}
                        style={{ padding: 4, marginRight: 6, cursor: "grab" } as any}
                      >
                        <Feather name="menu" size={14} color={themeColors.textSecondary} />
                      </Pressable>
                      <TouchableOpacity
                        style={{ flexDirection: "row", alignItems: "center", flex: 1 }}
                        onPress={() => toggleColumn(key)}
                      >
                        <RNView
                          style={{
                            width: 20,
                            height: 20,
                            borderRadius: 4,
                            borderWidth: 1.5,
                            borderColor: isVisible ? themeColors.primary : themeColors.textSecondary,
                            backgroundColor: isVisible ? themeColors.primary : themeColors.surface,
                            justifyContent: "center",
                            alignItems: "center",
                            marginRight: 10,
                          }}
                        >
                          {isVisible && (
                            <Feather name="check" size={13} color={themeColors.surface} />
                          )}
                        </RNView>
                        <Text
                          style={{
                            fontSize: 14,
                            color: isVisible ? themeColors.text : themeColors.textSecondary,
                            fontWeight: isVisible ? "600" : "400",
                          }}
                        >
                          {String(value.name || key)}
                        </Text>
                      </TouchableOpacity>
                    </RNView>
                  );
                })}
              </ScrollView>
              {columnOrder.length > 0 && (
                <>
                  <RNView style={{ height: 1, backgroundColor: themeColors.surfaceSubtle, marginTop: 4 }} />
                  <TouchableOpacity
                    style={{
                      flexDirection: "row",
                      alignItems: "center",
                      justifyContent: "center",
                      paddingVertical: 10,
                      paddingHorizontal: 16,
                    }}
                    onPress={resetColumnOrder}
                  >
                    <Feather name="rotate-ccw" size={13} color={themeColors.textSecondary} />
                    <Text style={{ fontSize: 13, color: themeColors.textSecondary, fontWeight: "600", marginLeft: 6 }}>
                      Reset Order
                    </Text>
                  </TouchableOpacity>
                </>
              )}
            </TouchableOpacity>
          </TouchableOpacity>
        </Modal>
      )}
    </>
  );
};

function createStyles(colors: any) {
  return StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "rgba(0,0,0,0.15)",
    paddingVertical: 16,
    paddingHorizontal: 8,
  },
  modalContainer: {
    backgroundColor: colors.surface,
    padding: 20,
    elevation: 12,
    borderRadius: 12,
    width: "100%",
    maxHeight: "95%",
  },
  modalContainerSecond: {
    backgroundColor: colors.surface,
    elevation: 12,
    borderRadius: 12,
    padding: 16,
    width: "100%",
    height: "95%",
  },
  titleContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    backgroundColor: colors.surface,
    paddingVertical: 12,
    marginBottom: 12,
  },
  titleText: {
    fontSize: 18,
    fontWeight: "700",
    color: colors.text,
  },
  titleTextSmall: {
    fontSize: 14,
  },
  searchContainer: {
    flexDirection: "row",
    alignItems: "center",
  },
  searchInput: {
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 4,
    padding: 8,
    width: 200,
    color: colors.text,
  },
  searchButton: {
    backgroundColor: colors.surfaceSubtle,
    borderWidth: 1,
    borderColor: colors.border,
    padding: 8,
    borderRadius: 4,
    marginLeft: 5,
  },
  actionButtonsContainer: {
    flexDirection: "row",
    justifyContent: "flex-end",
    marginBottom: 12,
    gap: 10,
  },
  actionButton: {
    backgroundColor: colors.primary,
    paddingVertical: 8,
    paddingHorizontal: 15,
    borderRadius: 5,
    marginLeft: 10,
    width: 67,
  },
  disabledActionButton: {
    backgroundColor: colors.surfaceSubtle,
    borderWidth: 1,
    borderColor: colors.border,
    paddingVertical: 8,
    paddingHorizontal: 15,
    borderRadius: 5,
    marginLeft: 10,
    width: 67,
  },
  actionButtonText: {
    fontSize: 10,
    color: colors.surface,
    fontWeight: 700,
  },
  formSection: {
    marginTop: 20,
    paddingHorizontal: 10,
  },
  footer: {
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "center",
    marginTop: 20,
    padding: 25,
  },
  footerSmall: {
    flexDirection: "row",
    justifyContent: "flex-start",
  },
  closeButton: {
    padding: 10,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 5,
    width: 120,
    alignItems: "center",
  },
  closeButtonText: {
    color: colors.text,
    fontWeight: "600",
  },
  buttonSmall: {
    padding: 8,
    paddingHorizontal: 12,
  },
  buttonTextSmall: {
    fontSize: 12,
  },
  containerContent: {
    flexDirection: "column",
    alignSelf: "flex-start",
    width: "100%",
    paddingHorizontal: 0,
    paddingBottom: 0,
  },
  tableScrollWrapper: {
    flex: 1,
    width: "100%",
  },
  modalContentScroll: {
    flex: 1,
  },
  modalContentScrollContainer: {
    paddingBottom: 20,
  },
  stickyHeaderSection: {
    backgroundColor: colors.surface,
    paddingBottom: 12,
    zIndex: 10,
  },
  scrollBodySection: {
    paddingTop: 12,
  },
  scrollBodySectionMobile: {
    paddingTop: 4,
  },
  modalContainerMobile: {
    padding: 10,
    borderRadius: 10,
  },
  titleContainerMobile: {
    flexDirection: "column",
    alignItems: "stretch",
    paddingVertical: 8,
    marginBottom: 4,
    gap: 6,
  },
  searchContainerMobile: {
    width: "100%",
  },

  filterLabel: {
    fontSize: 12,
    fontWeight: "500",
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    marginHorizontal: 4,
    backgroundColor: colors.surfaceSubtle,
    borderWidth: 1,
    borderColor: colors.border,
    color: colors.textSecondary,
    overflow: "hidden",
  },

  selectedFilter: {
    fontSize: 12,
    fontWeight: "600",
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 20,
    marginHorizontal: 4,
    backgroundColor: colors.primary,
    color: colors.surface,
    overflow: "hidden",
  },
});
}

export default ChinaTrackerAdvancedSearch;
